import { GlobalStyles, Stack, Typography, useTheme } from "@mui/material";
import { PickersActionBar, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import EventIcon from '@mui/icons-material/Event';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import dayjs, { Dayjs } from "dayjs";
import 'dayjs/locale/vi';
import { styled } from "@mui/material/styles";
import DrawerBottom from "../../../ui/DrawerBottom";
import IconCircleWrapper from "../../../ui/IconCircleWrapper";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const CustomDatePickerWrapper = styled('div')`
  .MuiOutlinedInput-root {
    display: none;
  }
  .MuiPickersToolbar-root {
    display: none;
  }
  .MuiPickersDay-root {
    font-size: 16px;
    color: #333;
  }
  .Mui-selected {
    background-color: #ff66a3 !important;
    color: white !important;
  }
  .MuiTypography-root {
    font-weight: bold;
  }
  .MuiPickersCalendarHeader-root {
    background-color: #ffccda;
    border-radius: 20px;
    padding: 10px;
  }
  .MuiPickersCalendarHeader-label {
    font-size: 18px;
    font-weight: bold;
  }
`;

interface DatePickerComponentProps {
  datePicker: Dayjs | undefined;
  setDatePicker: (date: Dayjs | undefined) => void;
  openDatePicker: boolean;
  setOpenDatePicker: (open: boolean) => void;
  label?: string;
  icon?: React.ReactNode;
  readOnly?: boolean;
  clearable?: boolean;
}

const DatePickerComponent = ({
  datePicker,
  setDatePicker,
  openDatePicker,
  setOpenDatePicker,
  label,
  icon,
  readOnly = false,
  clearable = true
}: DatePickerComponentProps) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState(dayjs(datePicker));
  const { t, i18n } = useTranslation(['common', 'transactions']);

  return (
    <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
      <Stack direction="row" spacing={theme.spacing(2)} alignItems="center" pb={theme.spacing(2)}>
        {icon || (
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
            <EventIcon sx={{ fontSize: 18 }} />
          </IconCircleWrapper>
        )}
      </Stack>
      <Stack
        onClick={readOnly ? undefined : () => setOpenDatePicker(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenDatePicker(true);
          }
        }}
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="center"
        justifyContent="space-between"
        width={"100%"}
        borderBottom={`1px solid ${theme.palette.divider}`}
        pb={theme.spacing(2)}
        sx={{ cursor: "pointer" }}
      >
        <Typography variant="body1" color="secondary">
          {label || t('date', { ns: 'transactions' })}
        </Typography>
        <Stack
          style={{ cursor: readOnly ? "default" : "pointer", fontWeight: "bold" }}
          direction="row"
          alignItems="center"
        >
          {datePicker?.isSame(dayjs(), "day") ? t('today', { ns: 'transactions' }) : datePicker?.format("DD/MM/YYYY")}

          {!readOnly && (
            <>
              <KeyboardArrowLeftIcon
                onClick={(e) => {
                  e.stopPropagation();
                  if (datePicker) {
                    setDatePicker(datePicker.subtract(1, "day"));
                  }
                }}
              />
              <KeyboardArrowRightIcon
                onClick={(e) => {
                  e.stopPropagation();
                  if (datePicker) {
                    setDatePicker(datePicker.add(1, "day"));
                  }
                }}
              />
            </>
          )}
        </Stack>
      </Stack>

      <DrawerBottom
        open={openDatePicker}
        onClose={() => setOpenDatePicker(false)}
        title={t('select_date', { ns: 'transactions' })}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={i18n.language === 'vi' ? 'vi' : 'en'}
          localeText={{
            okButtonLabel: t('select_date_accept', { ns: 'transactions' }),
            clearButtonLabel: t('select_date_clear', { ns: 'transactions' })
          }}>
          <CustomDatePickerWrapper>
            <GlobalStyles
              styles={{
                '.MuiDayCalendar-weekDayLabel': {
                  fontSize: '14px',
                  minWidth: '36px',
                  position: 'relative',
                  visibility: 'hidden',
                },
                '.MuiDayCalendar-weekDayLabel::after': {
                  visibility: 'visible',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: '#555',
                },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(1)::after': { content: i18n.language === 'vi' ? '"T2"' : '"Su"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(2)::after': { content: i18n.language === 'vi' ? '"T3"' : '"Mo"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(3)::after': { content: i18n.language === 'vi' ? '"T4"' : '"Tu"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(4)::after': { content: i18n.language === 'vi' ? '"T5"' : '"We"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(5)::after': { content: i18n.language === 'vi' ? '"T6"' : '"Th"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(6)::after': { content: i18n.language === 'vi' ? '"T7"' : '"Fr"' },
                '.MuiDayCalendar-weekDayLabel:nth-of-type(7)::after': { content: i18n.language === 'vi' ? '"CN"' : '"Sa"' },
              }}
            />
            <StaticDatePicker
              value={selectedDate}
              views={['year', 'month', 'day']}
              openTo="day"
              onChange={(newValue: any) => {
                setSelectedDate(newValue);
                setDatePicker(dayjs(newValue));
                setOpenDatePicker(false);
              }}
              slots={{
                actionBar: (props) => {
                  const handleClear = () => {
                    setDatePicker(undefined);
                    setOpenDatePicker(false);
                  };
            
                  return (
                    <PickersActionBar
                      {...props}
                      actions={clearable ? ['clear'] : []}
                      onClear={handleClear}
                      sx={{
                        '& .MuiButton-root': {
                          fontSize: '18px',
                          paddingRight: '20px',
                          '&:hover': {
                            backgroundColor: '#e0558f',
                          },
                        },
                      }}
                    />
                  )
                }
              }}
            />
          </CustomDatePickerWrapper>
        </LocalizationProvider>
      </DrawerBottom>
    </Stack>
  );
};

export default DatePickerComponent; 